import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Instagram, Facebook, Heart, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import Logo from "./Logo";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [pages, setPages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    api("/settings").then(setSettings).catch(() => {});
    api("/pages").then((p) => setPages(p.filter((x) => x.in_menu))).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = [
    ...(settings?.nav_items || []),
    ...pages.map((p) => ({ label: p.title, href: `/page/${p.slug}` })),
  ];

  const go = (href) => {
    setOpen(false);
    navigate(href);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-gold/15 shadow-[0_8px_30px_rgba(9,9,11,0.06)]" : "bg-transparent"
      }`}
    >
      <div
        data-testid="topbar-announcement"
        className={`bg-ink overflow-hidden transition-[max-height,opacity] duration-500 ${
          scrolled ? "max-h-0 opacity-0" : "max-h-12 opacity-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-10 flex items-center justify-center gap-2 text-[9px] md:text-[10px] uppercase tracking-[0.22em] font-bold">
          <Heart size={11} className="text-champagne shrink-0" fill="currentColor" />
          <span className="text-cream/80 truncate">
            <span className="hidden sm:inline">Brands, vendors &amp; venues — </span>Get featured on TBOI
          </span>
          <Link
            to="/get-featured"
            data-testid="topbar-get-featured-cta"
            className="ml-1 inline-flex items-center gap-1 text-champagne hover:text-gold transition-colors duration-300 shrink-0"
          >
            Apply now <ArrowUpRight size={11} />
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" aria-label="The Brides of India home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {items.map((item, i) => {
            const isFeature = item.href === "/get-featured";
            return (
              <Link
                key={item.href + i}
                to={item.href}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={
                  isFeature
                    ? "inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] uppercase tracking-[0.18em] font-bold text-gold border border-gold/50 rounded-full px-4 py-1.5 hover:bg-gold hover:text-ink transition-colors duration-300"
                    : "whitespace-nowrap text-[11px] uppercase tracking-[0.18em] font-bold text-ink/70 hover:text-gold transition-colors duration-300"
                }
              >
                {isFeature && <Heart size={11} fill="currentColor" />}
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {settings?.instagram_url && (
            <a href={settings.instagram_url} target="_blank" rel="noreferrer" data-testid="nav-instagram-link" aria-label="Instagram"
              className="text-ink/60 hover:text-rose transition-colors duration-300">
              <Instagram size={17} />
            </a>
          )}
          {settings?.facebook_url && (
            <a href={settings.facebook_url} target="_blank" rel="noreferrer" data-testid="nav-facebook-link" aria-label="Facebook"
              className="text-ink/60 hover:text-rose transition-colors duration-300">
              <Facebook size={17} />
            </a>
          )}
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="text-[10px] uppercase tracking-[0.2em] font-bold bg-ink text-cream rounded-full px-5 py-2.5 hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            Login
          </Link>
        </div>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-ink"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gold/15 px-6 py-6 flex flex-col gap-5">
          {items.map((item, i) => {
            const isFeature = item.href === "/get-featured";
            return (
              <button
                key={item.href + i}
                onClick={() => go(item.href)}
                data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`text-left font-display uppercase text-2xl inline-flex items-center gap-2.5 ${
                  isFeature ? "text-gold" : "text-ink"
                }`}
              >
                {isFeature && <Heart size={18} className="text-gold" fill="currentColor" />}
                {item.label}
              </button>
            );
          })}
          <button onClick={() => go("/admin")} data-testid="nav-mobile-admin-link" className="text-left text-xs uppercase tracking-[0.25em] text-gold font-bold">
            Login
          </button>
        </div>
      )}
    </header>
  );
}
